// Persistence (docs/08 §8.3, §8.6). Songs (settings + layer metadata + event
// layers) are small → localStorage, one key per song id, with an index and a
// "last song" pointer. Audio-layer blobs are large → IndexedDB. All local;
// nothing is uploaded.

import type { Settings } from "./song.ts";
import type { NoteEvent } from "../util/types.ts";

interface SerializedBase {
  id: string;
  name: string;
  color: string;
  muted: boolean;
  solo: boolean;
  volumeDb: number;
  pan: number;
  sendAmount: number;
  bars: number;
  locked?: boolean; // optional: older saves predate the lock feature
}

export interface SerializedEventLayer extends SerializedBase {
  kind: "event";
  instrumentId: string;
  events: NoteEvent[];
}

export interface SerializedAudioLayer extends SerializedBase {
  kind: "audio";
  audioRef: string;
  durationSec: number;
}

export type SerializedLayer = SerializedEventLayer | SerializedAudioLayer;

export interface SerializedSong {
  schemaVersion: number;
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  settings: Settings;
  layers: SerializedLayer[];
}

export interface SongMeta {
  id: string;
  title: string;
  updatedAt: number;
}

const SONG_PREFIX = "loopfield:song:";
const INDEX_KEY = "loopfield:songs";
const LAST_KEY = "loopfield:lastSongId";
const DB_NAME = "loopfield";
const DB_VERSION = 1;
const AUDIO_STORE = "audio";

// --- Song JSON (localStorage) --------------------------------------------

export function saveSong(song: SerializedSong): void {
  try {
    localStorage.setItem(SONG_PREFIX + song.id, JSON.stringify(song));
    upsertIndex({ id: song.id, title: song.title, updatedAt: song.updatedAt });
  } catch {
    // storage full / unavailable — non-fatal
  }
}

export function setLastSong(id: string): void {
  try {
    localStorage.setItem(LAST_KEY, id);
  } catch {
    // ignore
  }
}

export function loadSong(id: string): SerializedSong | null {
  try {
    const raw = localStorage.getItem(SONG_PREFIX + id);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SerializedSong;
    if (!parsed || !Array.isArray(parsed.layers)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function listSongs(): SongMeta[] {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    const list = raw ? (JSON.parse(raw) as SongMeta[]) : [];
    return list.sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

export function getLastSongId(): string | null {
  try {
    return localStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
}

export async function deleteSong(id: string): Promise<void> {
  const song = loadSong(id);
  if (song) {
    for (const layer of song.layers) {
      if (layer.kind === "audio" && layer.audioRef) await deleteAudioBlob(layer.audioRef);
    }
  }
  try {
    localStorage.removeItem(SONG_PREFIX + id);
    const next = listSongs().filter((m) => m.id !== id);
    localStorage.setItem(INDEX_KEY, JSON.stringify(next));
    if (getLastSongId() === id) {
      const fallback = next[0]?.id;
      if (fallback) localStorage.setItem(LAST_KEY, fallback);
      else localStorage.removeItem(LAST_KEY);
    }
  } catch {
    // ignore
  }
}

function upsertIndex(meta: SongMeta): void {
  const list = listSongs().filter((m) => m.id !== meta.id);
  list.push(meta);
  localStorage.setItem(INDEX_KEY, JSON.stringify(list));
}

// --- Audio blobs (IndexedDB) ---------------------------------------------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(AUDIO_STORE)) db.createObjectStore(AUDIO_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(AUDIO_STORE, mode);
    const req = fn(tx.objectStore(AUDIO_STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
    tx.oncomplete = () => db.close();
  });
}

export async function saveAudioBlob(ref: string, blob: Blob): Promise<void> {
  await withStore("readwrite", (s) => s.put(blob, ref));
}

export async function loadAudioBlob(ref: string): Promise<Blob | null> {
  const result = await withStore<Blob | undefined>("readonly", (s) => s.get(ref) as IDBRequest<Blob | undefined>);
  return result ?? null;
}

export async function deleteAudioBlob(ref: string): Promise<void> {
  await withStore("readwrite", (s) => s.delete(ref));
}
